import React from "react";
import {createTheme} from '@mui/material/styles';
import "@fontsource/roboto" // According to fontsource and material-ui we should import only needed weights. But importing the weight css did not work
import "@fontsource/roboto/100.css"
import "@fontsource/roboto/300.css"
import "@fontsource/roboto/500.css"
import "@fontsource/roboto/700.css"
import "@fontsource/roboto/900.css"
import "@fontsource/nunito-sans"
import "@fontsource/nunito-sans/200.css"
import "@fontsource/nunito-sans/400.css"
import "@fontsource/nunito-sans/700.css"
import "@fontsource/nunito-sans/900.css"
import {common} from "@mui/material/colors";
import {colors} from "@mui/material";

/**
 * We enhance the standard theme palette with our own colors.
 * See https://material-ui.com/customization/palette/#adding-new-colors
 *
 * Here we add two more background colors.
 */
declare module "@mui/material/styles/createPalette" {
  // noinspection JSUnusedGlobalSymbols
  interface TypeBackground {
    light: React.CSSProperties['color'];
    highlight: React.CSSProperties['color'];
  }

  // noinspection JSUnusedGlobalSymbols
  interface TypeBackground {
    light: React.CSSProperties['color'];
    highlight: React.CSSProperties['color'];
  }
}

/**
 * We use roboto font for all labels, titles, texts etc.
 *
 * You can easily  use the font in your style def by just writing "...robotoBase"
 */
export const robotoBase = {
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
};

/**
 * We use nunito font for all displayed data (coming from database) input fields etc
 *
 * You can easily  use the font in your style def by just writing "...nunitoBase"
 */
// eslint-disable-next-line
export const nunitoBase = {
  fontFamily: "Nunito Sans, Helvetica, Arial, sans-serif",
};

/**
 * All colors used inside account service
 */
export const palette = {
  primary: '#2575D0',
  primaryTransparent: '#2575D022',
  borderColor: '#707070',
  borderColorDisabled: 'rgba(112,112,112,0.64)',
  highlight: '#0245a3',
  dark: '#1E1E1E',
  normal: common.black,
  background: common.white,
  light: colors.grey["200"],
  greyText: colors.grey["600"],
  highlightText: '#0245a3',
  deepBlue: '#0245a3',
  kurzBlue: '#2B2967',
  brightYellow: '#f0ac27',
  greyTextOpacity: {
    opacity: 0.64,
  },
  lightGreyTextOpacity: {
    opacity: 0.32,
  }

};

export const outlined = {
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: palette.borderColor,
}

/**
 * The final theme definition. We use a dark theme for now with having in mind to enable a switch to a light theme later
 */
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: common.black,
    },
    secondary: {
      main: palette.highlight,
      contrastText: common.white
    },
    background: {
      default: common.white,
      paper: common.white,
      light: colors.grey["200"],
      highlight: palette.highlight,
    },
    text: {
      primary: common.black,
      secondary: palette.highlight,
      disabled: palette.greyText,
    }
  },
  typography: {
    ...robotoBase,
    h1: {
      fontSize: 32,
      letterSpacing: 0,
      fontWeight: 'normal',
    },
    h2: {
      fontSize: 24,
      letterSpacing: 0,
      fontWeight: 'bold',
    },
    h3: {
      fontSize: 20,
      letterSpacing: 0.15,
      fontWeight: 'bolder',
    },
    h4: {
      fontSize: 18,
      letterSpacing: 0.15,
      fontWeight: 'bolder',
    },
    h5: {
      fontSize: 16,
      letterSpacing: 0.1,
      fontWeight: 500,
    },
    h6: {
      fontSize: 14,
      letterSpacing: 0.1,
      fontWeight: 'bold',
    },
  },
  components: {
    MuiFormLabel: {
      styleOverrides: {
        // Default `text.secondary` is the highlight blue, which makes idle/shrunk
        // form labels look "active". Force the label color to greyText so labels
        // read as neutral metadata. Focused state still picks up `primary.main`
        // (black) from MUI as a focus indicator; error/disabled stay default.
        root: {
          color: palette.greyText,
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          ...palette.greyTextOpacity,
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          ...nunitoBase
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        label: {
          ...nunitoBase
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          ...nunitoBase
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.blue["700"]
        }
      }
    }
  }
});
